import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Modal, Upload, Breadcrumb, Typography, Spin, Select } from 'antd';
import config from '../../config';
import {
  getBase64,
  formItemLayout,
  unexpectedError,
  tailFormItemLayout,
  handleBackendFormErrors,
  backButton,
  beforeUpload,
  onRemove,
} from '../../common_functions';
import UploadTrigger from '../../common_functions/UploadTrigger';

const allowedPlateKey = e => {
  const { value, selectionStart } = e.target;
  const key = e.key;
  if (!/[\d.]/.test(key)) { e.preventDefault(); return; }
  if (key === '.' && value.includes('.')) { e.preventDefault(); return; }
  const [intPart = '', decPart] = value.split('.');
  if (key !== '.' && decPart === undefined && intPart.replace(/[^0-9]/g, '').length >= 9 && selectionStart <= intPart.length) {
    e.preventDefault(); return;
  }
  if (key !== '.' && decPart !== undefined && selectionStart > value.indexOf('.') && decPart.length >= 5) {
    e.preventDefault();
  }
};

// const BookIllustrations = props => {
const BookIllustrations = props => {
  const { getItems, getItem, saveItem, saveFile, getFile, showButton, roleType, tabs_permissions } = props;
  const { id } = useParams();
  const [bookIllustration, setBookIllustration] = useState({});
  const [loading, setLoading] = useState(false);
  const [book, setBook] = useState([]);
  const [negativeData, setNegativeData] = useState([]);
  const formRef = useRef(null);
  // Preview Image
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [imgPreview, setImgPreview] = useState('');
  const [previewTitle, setPreviewTitle] = useState(false);
  const [image, setImage] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        let { data: books } = await getItems('books', {
          fields: ['id,book_title'],
          sort: 'book_title',
          limit: -1,
        });
        setBook(books);
        let { data: negatives } = await getItems('object_negative', {
          fields: ['id', 'obj_name'],
          sort: 'obj_name',
          limit: -1,
        });
        setNegativeData(negatives);
      } catch (error) {
        console.error(error);
        unexpectedError(error);
      } finally {
        id === 'new' && setLoading(false);
      }
    }
    fetchData();
  }, []);
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        if (id !== 'new') {
          let { data } = await getItem('book_illustration', id, {
            fields: ['*', 'image.id', 'image.filename_download'],
          });
          if (data?.image?.id) {
            const { filename_download, id: fileID } = data.image;
            const fileData = await getFile(fileID);
            setImage([
              {
                uid: fileID,
                name: filename_download,
                url: fileData,
              },
            ]);
            data.image = fileID;
            setPreviewImage(fileData);
          }

          if (data.plate !== undefined && data.plate !== null) {
            data.plate = String(data.plate);
          }
          setBookIllustration(data);
          formRef?.current?.setFieldsValue(data);
        }
      } catch (error) {
        console.error(error);
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  let onFinish = async values => {
    try {
      setLoading(true);
      if (values?.image?.fileList?.[0]?.originFileObj) {
        const { fileList } = values.image;
        const formData = new FormData();
        formData.append('folder', process.env.book_images_folder);
        formData.append('file', fileList[0].originFileObj);

        let {
          data: { id: fileID },
        } = await saveFile(formData, bookIllustration?.image);
        values.image = fileID;
      }
      if (values.plate !== undefined && values.plate !== null) {
        values.plate = values.plate.trim();
      }
      let newState = { ...bookIllustration, ...values };
      const response = await saveItem('book_illustration', newState);
      if (response) {
        notification['success']({
          message: 'Book Illustration saved successfully!',
          duration: 2,
        });
        backButton(props, `/bibliography/book-illustrations`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };

  const handleCancel = () => setPreviewVisible(false);
  /**Handle file change**/
  const uploadFile = async ({ file, fileList }) => {
    try {
      setLoading(true);
      if (!(file instanceof File)) return;
      const url = await getBase64(fileList[0].originFileObj);
      setImage([
        {
          uid: '-1',
          name: file.name,
          status: 'done',
          url: url,
        },
      ]);
      setLoading(false);
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    }
  };
  const handlePreview = async file => {
    setPreviewImage('');
    setImgPreview('');
    const parts = file.name.split('.');
    const extension = parts.pop();
    if (extension == 'pdf') {
      setPreviewImage(file.url);
    } else {
      setImgPreview(file.url);
    }
    setPreviewTitle(file.name);
    setPreviewVisible(true);
  };
  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Bibliography", href: "/bibliography/book-illustrations" }, { title: id != 'new' ? `Edit Book Illustration:${id}` : 'Add Book Illustration' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id != 'new' ? `Edit Book Illustration:${id}` : 'Add Book Illustration'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="books"
                    label="Book"
                    rules={[
                      {
                        required: true,
                        message: 'Book is required!',
                      },
                    ]}
                  >
                    <Select showSearch allowClear placeholder="Select book" optionFilterProp="children">
                      {book.map(val => (
                        <Select.Option key={val.id} value={val.id}>
                          {val.book_title}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="plate"
                    label="Plate #"
                    rules={[
                      {
                        required: true,
                        message: 'Plate # is required!',
                      },
                      {
                        pattern: /^\d{1,9}(\.\d{1,5})?$/,
                        message: 'Plate # must be a number (max 9 digits before, 5 after decimal).',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter plate #"
                      onKeyPress={allowedPlateKey}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="negative"
                    label="Negative"
                    rules={[
                      {
                        required: true,
                        message: 'Negative is required!',
                      },
                    ]}
                  >
                    <Select showSearch allowClear placeholder="Select Negative" optionFilterProp="children">
                      {negativeData.map(neg => {
                        return <Select.Option key={neg.id} value={neg.id}>{!!neg.dummy ? `${neg.obj_name} - Draft` : neg.obj_name}</Select.Option>;
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="image_title"
                    label="Image Title Per Publication"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Image title per publication is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter image title per publication" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="image" label="Image">
                    <Upload
                      name="file"
                      listType="picture"
                      className="create_object"
                      accept={config.FileType.bookImage.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'bookImage');
                      }}
                      onRemove={onRemove}
                      showUploadList={{
                        showRemoveIcon: false,
                      }}
                      onPreview={handlePreview}
                      maxCount={1}
                      multiple={false}
                      onChange={uploadFile}
                      fileList={image}
                    >
                      <UploadTrigger fileType="bookImage" />
                    </Upload>
                  </Form.Item>
                </Col>
                <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel} width={800}>
                  <div style={{ textAlign: 'center' }}>
                    {!!imgPreview ? (
                      <img
                        alt="No Image"
                        style={{
                          width: '100%',
                          height: 'auto',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                        src={imgPreview}
                      />
                    ) : (
                      <iframe
                        src={previewImage}
                        style={{
                          width: '100%',
                          height: '500px',
                          border: 'none',
                        }}
                      />
                    )}
                  </div>
                </Modal>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/bibliography/book-illustrations')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {showButton(roleType, tabs_permissions.bibliography_book_illustrations, !!bookIllustration ? bookIllustration.id : '')}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default BookIllustrations;
